import React, { useState } from 'react'
import { Circle } from 'react-feather'
import { Controller, useForm } from 'react-hook-form'

import { ApplicantTaxAddress } from '@frame/ui-components/address/applicant-tax-address'
import { ExitPromptType } from '@frame/ui-components/exit-prompt'
import { Footer } from '@frame/ui-components/footer'
import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { A } from '@frame/ui-primitives/a'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { InfoCard } from '@frame/ui-primitives/info-card'
import { Input } from '@frame/ui-primitives/input'
import { P } from '@frame/ui-primitives/p'

import { InternalEventType } from '@frame/lib/api/events/types'
import { UpdateTaxInfoRequest } from '@frame/lib/api/tasks/types'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useShowExitPrompt } from '@frame/lib/hooks/useShowExitPrompt'
import { DataAttribute } from '@frame/types'

import { TaxFormValues } from './types'

export function TaxLink() {
  const showExitPrompt = useShowExitPrompt()
  const {
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<TaxFormValues>({
    defaultValues: {
      full_name: '',
      address: undefined,
    },
  })
  const { redirectToSessionStateRoute } = useAutoNavigate()
  const { actions } = useRootContext()
  const task = useDirectTask()
  const [isLoading, setIsLoading] = useState(false)
  const { handleEvent } = useEventHandler()

  const onSubmit = handleSubmit(async (formValues) => {
    setIsLoading(true)
    if (!task || !formValues.address || !formValues.address.place_id || !formValues.full_name) {
      throw new Error('Form has not been filled out correctly')
    }
    const request: UpdateTaxInfoRequest = {
      full_name: formValues.full_name,
      address_place_id: formValues.address.place_id,
    }
    actions
      .persistTaxInfo(task?.id, request)
      .then(() => {
        handleEvent(InternalEventType.IRS_TASK_SUBMITTED)
        redirectToSessionStateRoute()
      })
      .finally(() => {
        setIsLoading(false)
      })
  })

  const clearAddressErrors = () => {
    clearErrors('address')
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        showExitPrompt={showExitPrompt}
        exitPromptType={ExitPromptType.EMPLOYER}
        title="Link your tax data"
      />
      <Body
        data-cy={DataAttribute.POS_TAX_LINK}
        className="flex h-full flex-col justify-between p-5 pb-0"
        paddingTopOverride={8}
      >
        <div>
          <H2 className="mb-1 text-left">Let's link your tax data</H2>
          <P className="mb-5 text-base font-normal">Provide the following as listed on your most recent filing</P>
          <form data-cy={DataAttribute.IRS_FORM} onSubmit={onSubmit} className="flex flex-col space-y-4 bg-transparent">
            <Controller
              name="full_name"
              control={control}
              rules={{
                required: { value: true, message: 'Required' },
                validate: (value) => {
                  const parts = value.split(' ')
                  const atLeastTwoParts = parts.length > 1
                  const eachPartNonEmpty = parts.every((part) => part.length > 0)
                  return (atLeastTwoParts && eachPartNonEmpty) || 'Please enter your full name'
                },
              }}
              render={({ field: { value, name, onChange } }) => (
                <Input
                  data-cy={DataAttribute.IRS_FULL_NAME_INPUT}
                  value={value}
                  name={name}
                  label="Name"
                  placeholder="Full Name"
                  error={errors.full_name?.message}
                  onChange={onChange}
                />
              )}
            />
            <ApplicantTaxAddress control={control} clearErrors={clearAddressErrors} />
          </form>
        </div>
        <InfoCard className="bg-moss-200 px-4.5 py-3">
          <P variant="small" className="font-normal">
            By clicking to continue, you agree to authorize Mocha Mortgage to access your tax information through their
            partners Truework and Halcyon.
          </P>
          <div className="mt-4 flex w-full flex-row items-center justify-between">
            <A
              className="text-sm font-normal"
              href="https://app.truework.com/api/static/halcyon_1177_june_2024.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Terms
            </A>
            <Circle size={4} className="rounded-md bg-electric-700" />
            <A
              className="text-sm font-normal"
              href="https://app.truework.com/api/static/halcyon_8821_january_2021.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              View IRS Tax Authorization Form
            </A>
          </div>
        </InfoCard>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button onClick={onSubmit} intent="primary" disabled={isLoading}>
              {isLoading ? <LoadingSpinner /> : 'I consent to authorize access'}
            </Button>
          ),
        }}
      />
    </>
  )
}
